/**
 * Users API
 *
 * Handles all user-related API calls.
 * Uses Zod validation for critical endpoints.
 */

import { BaseApiClient, type ExpectedRefusal } from '../base';
import { API_ENDPOINTS } from '../endpoints';
import { ACCESS_GRANT_REFUSALS } from '../access-refusals';
import {
  GRANT_BLOCKS_ROLE_ERROR_CODE,
  listUsersResponseSchema,
  getUserResponseSchema,
  meResponseSchema,
  SeenReleaseAckSchema,
} from '@aica/api-types/schemas';
import type {
  UserWithStats,
  UsersListResponse,
  InviteUserResponse,
  UpdateUserRoleResponse,
  UserProjectsResponse,
  UpdateUserProjectsResponse,
} from '../types';
import type {
  AccessGrantAssignment,
  UserResponse,
  MeResponse,
} from '@aica/api-types/schemas';

/** 409 `grant_blocks_role` of `PATCH /users/:id/role` (in the `error` field): the person holds a grant — the screen says «отзовите доступ». */
const GRANT_BLOCKS_ROLE_REFUSALS: readonly ExpectedRefusal[] = [
  { status: 409, error: GRANT_BLOCKS_ROLE_ERROR_CODE },
];

export class UsersApi extends BaseApiClient {
  /**
   * Get current user (id + org roster + isOwner) - validated with Zod against
   * the real GET /users/me response shape.
   */
  async getMe(): Promise<MeResponse> {
    return this.validatedRequest(API_ENDPOINTS.USERS_ME, meResponseSchema);
  }

  /**
   * Отметить релиз показанным (#946).
   *
   * Отметка живёт на пользователе, а не в браузере: один и тот же человек
   * заходит и в веб, и во встроенное приложение CRM, и увидеть релиз должен
   * один раз, а не один раз на браузер.
   */
  async markReleaseSeen(version: string): Promise<{ lastSeenRelease: string }> {
    // validatedRequest, а не request: ответ проверяется схемой, как и у
    // соседнего getMe(). Без проверки воркер, отдавший конверт ошибки или
    // изменивший форму, был бы выдан вызывающему за успешный результат.
    return this.validatedRequest(
      API_ENDPOINTS.USERS_ME_SEEN_RELEASE,
      SeenReleaseAckSchema,
      {
        method: 'POST',
        body: JSON.stringify({ version }),
      }
    );
  }

  /**
   * Get users list - validated with Zod
   */
  async getUsers(params?: {
    role?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }): Promise<UsersListResponse> {
    const path = `${API_ENDPOINTS.USERS}${this.buildQuery(params)}`;
    const rawData = await this.authenticatedFetch<Record<string, unknown>>(
      this.url(path)
    );

    const validated = listUsersResponseSchema.parse(rawData);
    return validated as unknown as UsersListResponse;
  }

  /**
   * Get user by ID - validated with Zod
   */
  async getUserById(id: string): Promise<UserWithStats> {
    const validated = await this.validatedRequest<{ user: UserResponse }>(
      API_ENDPOINTS.USERS_BY_ID(id),
      getUserResponseSchema
    );
    return validated.user as unknown as UserWithStats;
  }

  async inviteUser(data: {
    email: string;
    name?: string;
    role: string;
    /**
     * Projects a supervisor oversees. Required for `supervisor` and rejected
     * for every other role — the backend's `inviteUserRequestSchema` enforces
     * both, because a supervisor without a `project_users` row can list calls
     * but cannot open one.
     */
    projectIds?: string[];
    /**
     * Invite with a narrow analytics access (E1.5 PR 5b): a `supervisor` or
     * an `agent` without projects, the membership and the grant written
     * together. Sent only while `enableAccessGrants` resolves on (tier and flag, see
     * the catalog note of `access.analytics_grants`).
     */
    accessGrant?: AccessGrantAssignment;
  }): Promise<InviteUserResponse> {
    return this.request<InviteUserResponse>(API_ENDPOINTS.USERS_INVITE, {
      method: 'POST',
      body: JSON.stringify(data),
      expectedRefusals: ACCESS_GRANT_REFUSALS,
    });
  }

  async deleteUser(userId: string): Promise<{ success: boolean }> {
    return this.request<{ success: boolean }>(API_ENDPOINTS.USERS_BY_ID(userId), {
      method: 'DELETE',
    });
  }

  async updateUserRole(userId: string, role: string): Promise<UpdateUserRoleResponse> {
    return this.request<UpdateUserRoleResponse>(API_ENDPOINTS.USERS_ROLE(userId), {
      method: 'PATCH',
      body: JSON.stringify({ role }),
      expectedRefusals: GRANT_BLOCKS_ROLE_REFUSALS,
    });
  }

  async getUserProjects(userId: string): Promise<UserProjectsResponse> {
    return this.request<UserProjectsResponse>(API_ENDPOINTS.USERS_PROJECTS(userId));
  }

  /**
   * Ids, not roles. This request grants and revokes oversight; project 'admin'
   * belongs to whoever created the project and is not handed out from any
   * screen. Mirrors `updateProjectSupervisors` on the project side.
   *
   * The key must stay `projectIds` — the backend reads exactly that, and the
   * previous mismatch here (`project_assignments` against a schema expecting
   * camelCase) meant no assignment ever saved in production.
   */
  async updateUserProjects(
    userId: string,
    projectIds: string[]
  ): Promise<UpdateUserProjectsResponse> {
    return this.request<UpdateUserProjectsResponse>(
      API_ENDPOINTS.USERS_PROJECTS(userId),
      {
        method: 'PATCH',
        body: JSON.stringify({ projectIds }),
      }
    );
  }
}
